/**
 * Sticky add-to-cart — compact single variant dropdown (Glazee reference).
 */
import { findVariant } from '@theme/comfort-bra-bundle-dropdowns';

const CHEVRON_SVG =
  '<svg class="cb-sticky-variant-dd__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

(function () {
  if (!document.querySelector('.comfort-bra-promo-section')) return;

  const data = (() => {
    const el = document.getElementById('comfort-bra-product-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  })();

  if (!data) return;

  const sticky = document.querySelector('sticky-add-to-cart');
  if (!sticky) return;

  const bar = sticky.querySelector('[ref="stickyBar"]') || sticky.querySelector('.sticky-add-to-cart__bar');
  if (!bar) return;

  let built = false;
  /** @type {HTMLElement | null} */
  let dropdownEl = null;
  /** @type {HTMLElement | null} */
  let triggerLabel = null;
  let menuOpen = false;

  function getBundles() {
    return document.querySelector('comfort-bra-bundles');
  }

  function getSwatch(colorName) {
    return data.colors.find((c) => c.name === colorName)?.swatch || null;
  }

  function getCurrentSelection() {
    const bundles = getBundles();
    if (bundles?.getBundleState) {
      const sel = bundles.getBundleState().selections[0];
      if (sel) return sel;
    }
    return { color: data.colors[0]?.name, size: data.sizes[0] };
  }

  function formatLabel(color, size) {
    if (!color && !size) return '';
    if (!size) return color || '';
    if (!color) return size || '';
    return `${color} / ${size}`;
  }

  function getVariantOptions() {
    return data.variants.map((variant) => ({
      color: variant.color,
      size: variant.size,
      available: variant.available,
      label: formatLabel(variant.color, variant.size),
      swatch: getSwatch(variant.color),
    }));
  }

  function closeMenu() {
    if (!dropdownEl) return;
    const menu = dropdownEl.querySelector('.cb-sticky-variant-dd__menu');
    const trigger = dropdownEl.querySelector('.cb-sticky-variant-dd__trigger');
    if (menu) menu.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    dropdownEl.classList.remove('is-open');
    menuOpen = false;
  }

  function openMenu() {
    if (!dropdownEl) return;
    const menu = dropdownEl.querySelector('.cb-sticky-variant-dd__menu');
    const trigger = dropdownEl.querySelector('.cb-sticky-variant-dd__trigger');
    if (menu) menu.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    dropdownEl.classList.add('is-open');
    menuOpen = true;
  }

  function updateTrigger() {
    if (!triggerLabel) return;
    const { color, size } = getCurrentSelection();
    triggerLabel.textContent = formatLabel(color, size);
  }

  function renderMenu() {
    if (!dropdownEl) return;
    const menu = dropdownEl.querySelector('.cb-sticky-variant-dd__menu');
    if (!menu) return;

    const current = getCurrentSelection();
    menu.innerHTML = '';

    getVariantOptions().forEach((opt) => {
      const item = document.createElement('li');
      item.className = 'cb-sticky-variant-dd__option';
      item.setAttribute('role', 'option');
      if (!opt.available) item.classList.add('is-disabled');
      if (opt.color === current.color && opt.size === current.size) {
        item.setAttribute('aria-selected', 'true');
      }

      if (opt.swatch) {
        const swatch = document.createElement('span');
        swatch.className = 'cb-sticky-variant-dd__swatch';
        swatch.style.backgroundImage = `url(${opt.swatch}&width=64)`;
        item.appendChild(swatch);
      }

      const text = document.createElement('span');
      text.className = 'cb-sticky-variant-dd__option-text';
      text.textContent = opt.label;
      item.appendChild(text);

      item.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!opt.available) return;
        const bundles = getBundles();
        bundles?.setItemSelection?.(0, opt.color, opt.size);
        updateTrigger();
        closeMenu();
      });

      menu.appendChild(item);
    });
  }

  function buildStickyRef() {
    if (built) return;
    built = true;

    bar.classList.add('cb-sticky-bar--ref');

    const variantEl = sticky.querySelector('.sticky-add-to-cart__variant');
    if (variantEl) variantEl.hidden = true;

    const title = sticky.querySelector('.sticky-add-to-cart__title');
    if (title && !sticky.querySelector('.cb-sticky-stars')) {
      const stars = document.createElement('span');
      stars.className = 'cb-sticky-stars';
      stars.setAttribute('aria-hidden', 'true');
      stars.textContent = '★★★★★';
      title.insertAdjacentElement('afterend', stars);
    }

    const priceEl = sticky.querySelector('.sticky-add-to-cart__price');
    const buttonEl = sticky.querySelector('.sticky-add-to-cart__button');
    if (!priceEl || !buttonEl) return;

    dropdownEl = document.createElement('div');
    dropdownEl.className = 'cb-sticky-variant-dd';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cb-sticky-variant-dd__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', data.labels?.variant || 'Variant');

    triggerLabel = document.createElement('span');
    triggerLabel.className = 'cb-sticky-variant-dd__label';
    trigger.appendChild(triggerLabel);
    trigger.insertAdjacentHTML('beforeend', CHEVRON_SVG);

    const menu = document.createElement('ul');
    menu.className = 'cb-sticky-variant-dd__menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    dropdownEl.appendChild(trigger);
    dropdownEl.appendChild(menu);

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      if (menuOpen) {
        closeMenu();
      } else {
        renderMenu();
        openMenu();
      }
    });

    dropdownEl.addEventListener('click', (e) => e.stopPropagation());
    priceEl.insertAdjacentElement('afterend', dropdownEl);

    const addIcon = buttonEl.querySelector('.svg-wrapper');
    if (addIcon) addIcon.style.display = 'none';

    updateTrigger();

    document.addEventListener('click', () => closeMenu());
    document.addEventListener('comfort-bra:selection-change', updateTrigger);
  }

  const observer = new MutationObserver(() => {
    if (bar.getAttribute('data-stuck') === 'true') buildStickyRef();
  });
  observer.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });

  if (bar.getAttribute('data-stuck') === 'true') buildStickyRef();
})();
